import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight, ListMusic, Pause, Play } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api } from "./api";
import type { AudiobookManifest } from "./api-types";

interface BookMeta {
  id: string;
  title: string;
  author: string;
  coverUrl: string;
}

interface TrackTarget {
  number: number | null;
  kind: string;
}

interface PlayerValue {
  manifest: AudiobookManifest | null;
  book: BookMeta | null;
  activeIndex: number;
  playing: boolean;
  currentTime: number;
  duration: number;
  loading: boolean;
  error: string | null;
  load: (book: BookMeta, autoplay?: boolean, target?: TrackTarget) => Promise<void>;
  toggle: () => void;
  next: () => void;
  previous: () => void;
  select: (index: number, autoplay?: boolean) => void;
  seek: (seconds: number) => void;
}

const PlayerContext = createContext<PlayerValue | null>(null);

export function AudiobookPlayerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [manifest, setManifest] = useState<AudiobookManifest | null>(null);
  const [book, setBook] = useState<BookMeta | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tracks = manifest?.tracks ?? [];

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    void audio.play().then(() => setPlaying(true)).catch(() => {
      setPlaying(false);
      setError("Playback could not start.");
    });
  }, []);

  const select = useCallback((index: number, autoplay = false) => {
    const track = (manifest?.tracks ?? [])[index];
    if (!track) return;
    const audio = audioRef.current ?? new Audio();
    audioRef.current = audio;
    audio.preload = "metadata";
    audio.src = track.stream_url;
    setActiveIndex(index);
    setCurrentTime(0);
    setDuration(track.duration_ms / 1000);
    setError(null);
    if (book && location.pathname.startsWith(`/books/${book.id}/chapter/`)) {
      const segment = track.kind === "body" ? track.number : "front";
      navigate(`/books/${book.id}/chapter/${segment}`, { replace: true });
    }
    if (autoplay) play();
    else setPlaying(false);
  }, [book, location.pathname, manifest, navigate, play]);

  const next = useCallback(() => {
    if (!tracks.length) return;
    if (activeIndex >= tracks.length - 1) {
      audioRef.current?.pause();
      setCurrentTime(audioRef.current?.duration || duration);
      return;
    }
    select(activeIndex + 1, true);
  }, [activeIndex, duration, select, tracks.length]);

  const previous = useCallback(() => {
    if (!tracks.length) return;
    if ((audioRef.current?.currentTime ?? 0) > 8) {
      if (audioRef.current) audioRef.current.currentTime = 0;
      return;
    }
    select(Math.max(0, activeIndex - 1), true);
  }, [activeIndex, select, tracks.length]);

  useEffect(() => {
    const audio = audioRef.current ?? new Audio();
    audioRef.current = audio;
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onDuration = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => next();
    const onError = () => { setPlaying(false); setError("This chapter could not be played."); };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDuration);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDuration);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [duration, next]);

  const load = useCallback(async (meta: BookMeta, autoplay = false, target?: TrackTarget) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<AudiobookManifest>(`/api/books/${meta.id}/audiobook`);
      setBook(meta);
      setManifest(result);
      if (!result.ready || !result.tracks?.length) {
        setError("This audiobook is not ready yet.");
        return;
      }
      let parsed: { track?: string; seconds?: number } = {};
      try {
        const saved = window.localStorage.getItem(`dreambook.audiobook.${meta.id}`);
        parsed = saved ? JSON.parse(saved) as typeof parsed : {};
      } catch {
        // A malformed local resume marker must never prevent playback.
      }
      const targetIndex = target
        ? result.tracks.findIndex((track) => track.kind === target.kind && track.number === target.number)
        : -1;
      const savedIndex = result.tracks.findIndex((track) => track.id === parsed.track);
      const index = targetIndex >= 0 ? targetIndex : Math.max(0, savedIndex);
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.preload = "metadata";
      audio.src = result.tracks[index].stream_url;
      audio.currentTime = targetIndex >= 0 ? 0 : Math.max(0, Number(parsed.seconds || 0));
      setActiveIndex(index);
      setCurrentTime(audio.currentTime);
      setDuration(result.tracks[index].duration_ms / 1000);
      if (autoplay) play();
    } catch {
      setError("Could not open this audiobook.");
    } finally {
      setLoading(false);
    }
  }, [play]);

  useEffect(() => {
    const track = tracks[activeIndex];
    if (!book || !track) return;
    const timer = window.setTimeout(() => {
      window.localStorage.setItem(`dreambook.audiobook.${book.id}`, JSON.stringify({ track: track.id, seconds: currentTime }));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [activeIndex, book, currentTime, tracks]);

  useEffect(() => {
    if (!("mediaSession" in navigator) || !book) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: tracks[activeIndex]?.title ?? book.title,
      artist: book.author,
      album: book.title,
      artwork: [{ src: book.coverUrl }],
    });
    navigator.mediaSession.setActionHandler("play", play);
    navigator.mediaSession.setActionHandler("pause", () => audioRef.current?.pause());
    navigator.mediaSession.setActionHandler("nexttrack", next);
    navigator.mediaSession.setActionHandler("previoustrack", previous);
  }, [activeIndex, book, next, play, previous, tracks]);

  const value = useMemo<PlayerValue>(() => ({
    manifest, book, activeIndex, playing, currentTime, duration, loading, error, load,
    toggle: () => playing ? audioRef.current?.pause() : play(),
    next, previous, select,
    seek: (seconds) => { if (audioRef.current) audioRef.current.currentTime = seconds; },
  }), [activeIndex, book, currentTime, duration, error, load, loading, manifest, next, play, playing, previous, select]);

  return <PlayerContext.Provider value={value}>{children}<AudiobookMiniPlayer /></PlayerContext.Provider>;
}

export function useAudiobookPlayer() {
  const value = useContext(PlayerContext);
  if (!value) throw new Error("useAudiobookPlayer must be used inside AudiobookPlayerProvider");
  return value;
}

function clock(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
}

function AudiobookMiniPlayer() {
  const player = useAudiobookPlayer();
  const location = useLocation();
  const track = player.manifest?.tracks?.[player.activeIndex];
  if (!player.book || !track || location.pathname.endsWith("/listen") || location.pathname === "/audiobook-mockup") return null;
  const percent = player.duration ? Math.min(100, (player.currentTime / player.duration) * 100) : 0;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-paper/15 bg-ink-teal text-paper shadow-[0_-18px_42px_-24px_rgba(0,0,0,.85)]">
      <div className="h-1 bg-paper/15"><div className="h-full bg-terracotta-soft" style={{ width: `${percent}%` }} /></div>
      <div className="container-wide grid min-h-[78px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <div className="flex min-w-0 items-center gap-3">
          <img src={player.book.coverUrl} alt="" className="h-12 w-8 rounded-[2px] object-cover" />
          <div className="min-w-0"><p className="truncate font-body text-[14px] text-paper">{track.title}</p><p className="truncate font-mono text-[8px] uppercase tracking-smallcaps text-paper/55">{player.book.title}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={player.previous} aria-label="Previous audiobook chapter"><ChevronLeft size={21} /></button>
          <button type="button" onClick={player.toggle} aria-label={player.playing ? "Pause audiobook" : "Play audiobook"} className="grid h-11 w-11 place-items-center rounded-full bg-paper text-ink-teal">
            {player.playing ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" className="ml-0.5" />}
          </button>
          <button type="button" onClick={player.next} aria-label="Next audiobook chapter"><ChevronRight size={21} /></button>
        </div>
        <div className="hidden items-center justify-end gap-4 font-mono text-[8.5px] text-paper/55 sm:flex">
          <span>{clock(player.currentTime)} / {clock(player.duration)}</span>
          <Link to={`/books/${player.book.id}/listen`} className="inline-flex items-center gap-2 !text-paper/75 hover:!text-paper"><ListMusic size={15} /> Open player</Link>
        </div>
      </div>
    </div>
  );
}
